import { useMemo, useState } from "react";
import HeadToHeadCard from "../components/HeadToHeadCard";
import type { AppDataState } from "../api/useAppData";
import { formatLocalDateHeading, getLocalDateKey } from "../lib/dates";
import type { EnrichedFixture } from "../../shared/types";

type StatusFilter = "all" | "scheduled" | "live" | "finished";
type SortOrder = "asc" | "desc";

export default function FixturesPage({ state }: { state: AppDataState }) {
  const { data } = state;
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [sort, setSort] = useState<SortOrder>("asc");

  const grouped = useMemo(() => {
    if (!data) return [];
    const filtered =
      filter === "all"
        ? data.allFixtures
        : data.allFixtures.filter((f) => f.status === filter);
    return groupByLocalDate(filtered, sort);
  }, [data, filter, sort]);

  if (!data) return <p>Loading…</p>;

  return (
    <>
      <h1>Fixtures</h1>
      <div className="segmented" role="tablist" aria-label="Status filter">
        {(["all", "scheduled", "live", "finished"] as const).map((f) => (
          <button
            key={f}
            className={filter === f ? "active" : ""}
            onClick={() => setFilter(f)}
            role="tab"
            aria-selected={filter === f}
          >
            {label(f)}
          </button>
        ))}
      </div>

      <div className="segmented" role="tablist" aria-label="Sort order">
        {(["asc", "desc"] as const).map((s) => (
          <button
            key={s}
            className={sort === s ? "active" : ""}
            onClick={() => setSort(s)}
            role="tab"
            aria-selected={sort === s}
          >
            {s === "asc" ? "Earliest first" : "Latest first"}
          </button>
        ))}
      </div>

      {grouped.length === 0 ? (
        <div className="card empty-card">No fixtures.</div>
      ) : (
        grouped.map(({ date, fixtures }) => (
          <section key={date} className="section">
            <h2>{formatLocalDateHeading(fixtures[0].dateUtc)}</h2>
            <div className="h2h-grid">
              {fixtures.map((f) => (
                <HeadToHeadCard key={f.id} fixture={f} />
              ))}
            </div>
          </section>
        ))
      )}
    </>
  );
}

function label(f: StatusFilter) {
  if (f === "all") return "All";
  return f[0].toUpperCase() + f.slice(1);
}

function groupByLocalDate(fixtures: EnrichedFixture[], sort: SortOrder) {
  const dir = sort === "asc" ? 1 : -1;
  const map = new Map<string, EnrichedFixture[]>();
  for (const f of fixtures) {
    const key = getLocalDateKey(f.dateUtc);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(f);
  }
  return [...map.entries()]
    .map(([date, list]) => ({
      date,
      fixtures: list.sort(
        (a, b) =>
          dir *
          (new Date(a.dateUtc).getTime() - new Date(b.dateUtc).getTime())
      ),
    }))
    .sort((a, b) => dir * a.date.localeCompare(b.date));
}
